/**
 * Klien HTTP untuk seluruh aplikasi.
 *
 * Dua hal yang ditangani di sini dan tidak perlu diulang di tiap pemanggil:
 *
 *  1. **Perpanjangan sesi otomatis.** Saat access token kedaluwarsa (401),
 *     klien memanggil `/auth/refresh` lalu mengulang permintaan aslinya - 
 *     pengguna tidak pernah melihat sesi terputus. Beberapa permintaan yang
 *     gagal bersamaan berbagi satu proses refresh, bukan memicu banyak.
 *
 *  2. **Bentuk galat yang seragam.** Semua kegagalan menjadi `ApiError`
 *     dengan `code`, `message`, dan `details` - UI mencocokkan `code`, bukan
 *     teks pesan yang sewaktu-waktu bisa berubah.
 */

const BASE_URL = '/api/v1';


const pendengarKuota = new Set();

const beritahuKuotaHabis = (galat) => {
  pendengarKuota.forEach((fungsi) => {
    // Satu pendengar yang melempar tidak boleh menelan galat aslinya.
    try {
      fungsi(galat);
    } catch {
      /* diabaikan dengan sengaja */
    }
  });
};

/**
 * Kuota habis dijawab satu tempat, bukan di tiap pemanggil.
 *
 * Kuota bisa mentok di mana saja: menjalankan crawl, menyimpan berkas,
 * mengaktifkan jadwal. Kalau tiap pemanggil harus menanganinya sendiri, satu
 * saja yang lupa akan menampilkan pesan galat merah tanpa memberi tahu
 * pengguna apa yang bisa dilakukan. Di sinilah satu-satunya tempat seluruh
 * respons lewat, jadi di sinilah pemberitahuannya dipasang.
 */
const KODE_KUOTA = 'QUOTA_EXCEEDED';

export class ApiError extends Error {
  constructor({ message, code, status, details, requestId }) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.requestId = requestId;
  }

  /** Galat validasi per-field, siap dipasang ke react-hook-form. */
  get fieldErrors() {
    if (!Array.isArray(this.details)) return {};
    return Object.fromEntries(this.details.map((d) => [d.field, d.message]));
  }
}

let accessToken = null;
let organizationId = null;
let refreshPromise = null;
let onSessionExpired = null;

export const setAccessToken = (token) => {
  accessToken = token;
};
export const setOrganizationId = (id) => {
  organizationId = id;
};
export const getAccessToken = () => accessToken;
export const onUnauthorized = (handler) => {
  onSessionExpired = handler;
};

const buildHeaders = (body) => {
  const headers = { Accept: 'application/json' };
  if (body !== undefined && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (organizationId) headers['X-Organization-Id'] = organizationId;
  return headers;
};

const parseResponse = async (response) => {
  if (response.status === 204) return null;

  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    throw new ApiError({
      message: 'Server mengirim jawaban yang tidak bisa dibaca.',
      code: 'INVALID_RESPONSE',
      status: response.status,
    });
  }
};

/**
 * Menukar refresh cookie dengan access token baru.
 *
 * Dibungkus dedup: berapa pun pemanggil yang meminta bersamaan, hanya satu
 * permintaan yang benar-benar dikirim dan semuanya menunggu hasil yang sama.
 * Ini bukan sekadar optimasi - refresh token dirotasi sekali pakai, jadi dua
 * permintaan paralel dengan cookie yang sama akan terbaca server sebagai
 * token yang dicuri dan seluruh sesi ikut dicabut. (React StrictMode memanggil
 * efek dua kali di development, dan dua tab bisa melakukannya di produksi.)
 */
const performRefresh = async () => {
  const response = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    accessToken = null;
    throw new ApiError({
      message: 'Sesi Anda sudah berakhir. Silakan masuk kembali.',
      code: 'SESSION_EXPIRED',
      status: 401,
    });
  }

  const payload = await response.json();
  accessToken = payload.data.accessToken;
  return payload.data;
};

export const refreshSession = () => {
  refreshPromise = refreshPromise ?? performRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
};


const request = async (method, path, { body, params, signal, retry = true } = {}) => {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, value);
      }
    }
  }

  let response;
  try {
    response = await fetch(url, {
      method,
      headers: buildHeaders(body),
      body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'include',
      signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new ApiError({
      message: 'Tidak dapat menghubungi server. Periksa koneksi internet Anda.',
      code: 'NETWORK_ERROR',
      status: 0,
    });
  }

  // Access token kedaluwarsa → perpanjang sekali, lalu ulangi.
  if (response.status === 401 && retry && !path.startsWith('/auth/')) {
    try {
      await refreshSession();
      return request(method, path, { body, params, signal, retry: false });
    } catch (err) {
      onSessionExpired?.();
      throw err;
    }
  }

  const payload = await parseResponse(response);

  if (!response.ok) {
    const error = payload?.error ?? {};
    const galat = new ApiError({
      message: error.message ?? 'Terjadi kesalahan yang tidak diketahui.',
      code: error.code ?? 'UNKNOWN_ERROR',
      status: response.status,
      details: error.details,
      requestId: error.requestId,
    });

    if (galat.code === KODE_KUOTA) beritahuKuotaHabis(galat);

    throw galat;
  }

  return payload;
};


/** Mendaftar untuk diberi tahu saat kuota habis. Mengembalikan cara berhenti. */
export const dengarkanKuotaHabis = (fungsi) => {
  pendengarKuota.add(fungsi);
  return () => pendengarKuota.delete(fungsi);
};


export const api = {
  get: (path, options) => request('GET', path, options),
  post: (path, body, options) => request('POST', path, { ...options, body }),
  patch: (path, body, options) => request('PATCH', path, { ...options, body }),
  put: (path, body, options) => request('PUT', path, { ...options, body }),
  delete: (path, options) => request('DELETE', path, options),
  refreshSession,
};

/**
 * Mengunduh berkas dari API lalu menyerahkannya ke peramban.
 *
 * `window.open` TIDAK bisa dipakai untuk ini, dan itu bukan hal yang jelas:
 * ia membuka tab baru dengan permintaan GET polos, tanpa header apa pun.
 * Access token kita hidup di memori JavaScript (lihat ADR 0005), bukan di
 * cookie, jadi permintaan itu tiba di server tanpa identitas dan dijawab 401.
 * Gejalanya membingungkan - tab terbuka sekejap lalu tertutup, tanpa pesan.
 *
 * Karena itu berkasnya diambil lewat fetch biasa yang membawa token, ditampung
 * sebagai blob, lalu diklik sendiri sebagai tautan unduhan.
 *
 * Pertukarannya: berkas ditampung di memori peramban lebih dulu. Untuk ekspor
 * ratusan megabita itu terasa, tapi jauh lebih baik daripada tombol yang tidak
 * berfungsi sama sekali.
 */
export const unduhBerkas = async (path, namaBawaan = 'unduhan') => {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);

  let response = await fetch(url, { headers: buildHeaders(), credentials: 'include' });

  // Access token hanya berumur 15 menit; perpanjang sekali lalu ulangi,
  // persis seperti yang dilakukan `request`.
  if (response.status === 401) {
    await refreshSession();
    response = await fetch(url, { headers: buildHeaders(), credentials: 'include' });
  }

  if (!response.ok) {
    const payload = await parseResponse(response).catch(() => null);
    throw new ApiError({
      message: payload?.error?.message ?? 'Berkas gagal diunduh.',
      code: payload?.error?.code ?? 'DOWNLOAD_FAILED',
      status: response.status,
    });
  }

  // Nama berkas diambil dari header bila server menyebutkannya.
  const disposisi = response.headers.get('content-disposition') ?? '';
  const cocok = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposisi);
  const nama = cocok ? decodeURIComponent(cocok[1]) : namaBawaan;

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  const tautan = document.createElement('a');
  tautan.href = objectUrl;
  tautan.download = nama;
  document.body.appendChild(tautan);
  tautan.click();
  tautan.remove();

  // Dibebaskan setelah peramban sempat memulai unduhannya. Mencabutnya
  // seketika membatalkan unduhan di sebagian peramban.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);

  return nama;
};
